import { Ellipsis } from 'lucide-react';
import { ComponentProps, forwardRef, useEffect, useState } from 'react';
import * as ResizablePrimitive from 'react-resizable-panels';
import { cn } from '@/Domain/utils/cn';

const ResizablePanelGroup = ({
  className,
  ...props
}: ComponentProps<typeof ResizablePrimitive.PanelGroup>) => {
  // Enable the collapse/expand transition only after initial layout, so restoring the saved layout doesn't animate.
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setAnimated(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  return (
    <ResizablePrimitive.PanelGroup
      className={cn('flex h-full w-full data-[panel-group-direction=vertical]:flex-col', className)}
      data-panels-animated={animated ? '' : undefined}
      {...props}
    />
  );
};

const ResizablePanel = forwardRef<
  ResizablePrimitive.ImperativePanelHandle,
  ComponentProps<typeof ResizablePrimitive.Panel>
>(({ className, ...props }, ref) => (
  <ResizablePrimitive.Panel ref={ref} className={cn('h-full', className)} {...props} />
));

const ResizableHandle = ({
  withHandle,
  className,
  ...props
}: ComponentProps<typeof ResizablePrimitive.PanelResizeHandle> & {
  withHandle?: boolean;
}) => (
  <ResizablePrimitive.PanelResizeHandle
    className={cn(
      'relative flex w-[0.00001px] items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-[1px] after:-translate-x-1/2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-950 focus-visible:ring-offset-1 data-[panel-group-direction=vertical]:h-[1px] data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:after:left-0 data-[panel-group-direction=vertical]:after:h-[1px] data-[panel-group-direction=vertical]:after:w-full data-[panel-group-direction=vertical]:after:-translate-y-1/2 data-[panel-group-direction=vertical]:after:translate-x-0 [&[data-panel-group-direction=vertical]>div]:rotate-90',
      className,
    )}
    {...props}
  >
    {withHandle && (
      <div className="z-10 flex h-5 w-4 items-center justify-center rounded-md border border-border bg-card hover:bg-accent hover:border-accent transition-colors">
        <Ellipsis className="text-foreground-subtle h-4 w-4" />
      </div>
    )}
  </ResizablePrimitive.PanelResizeHandle>
);

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
